import { useCallback } from 'react';
import {
  Flex,
  RemoteImage,
  Typography,
  IconChevronRight,
  ColorValues,
  type SpacingSize,
} from '@banhala/ui-kit';
import type { AnyObject } from '../../../../../types';
import { bridge } from '../../../../../utils';

interface MarketCardProps {
  sno: number;
  profile: string | null;
  name: string;
  description: string | null;
  logging: AnyObject;
  profileSize: number;
  nameType: 'subtitle2' | 'subtitle3';
  descriptionType: 'body4' | 'body5';
  descriptionColor: 'content_tertiary' | 'content_secondary';
  height: number;
  paddingHorizontal: number;
  paddingVertical: number;
  textGap?: SpacingSize;
  pressable?: boolean;
  showChevron?: boolean;
  rightElement?: React.ReactNode;
}

export const MarketCard = ({
  sno,
  profile,
  name,
  description,
  logging,
  profileSize,
  nameType,
  descriptionType,
  descriptionColor,
  height,
  paddingHorizontal,
  paddingVertical,
  textGap = 2,
  pressable = true,
  showChevron = true,
  rightElement,
}: MarketCardProps) => {
  const handlePress = useCallback(() => {
    bridge.logAnalyticsEvent({
      name: 'CLICK_MARKET',
      analytics: logging.analytics,
      ably_only_log: { ...logging.ably_only_log, event_name: 'click' },
    });
    bridge.openDeeplink(`ably://markets/${sno}/`);
  }, [sno, logging]);

  return (
    <Flex.Row
      height={height}
      alignItems="center"
      padding={{ horizontal: paddingHorizontal, vertical: paddingVertical }}
      onPress={pressable ? handlePress : undefined}
    >
      {profile && (
        <Flex
          width={profileSize}
          height={profileSize}
          borderRadius={profileSize / 2}
          borderWidth={1}
          borderColor="border_transparent"
          overflow="hidden"
          margin={{ right: 8 }}
        >
          <RemoteImage uri={profile} width={profileSize} height={profileSize} />
        </Flex>
      )}

      <Flex flex={1} gap={textGap}>
        <Typography type={nameType} color="content_primary" numberOfLines={1}>
          {name}
        </Typography>
        {description && (
          <Typography
            type={descriptionType}
            color={descriptionColor}
            numberOfLines={1}
          >
            {description}
          </Typography>
        )}
      </Flex>

      {showChevron && (
        <IconChevronRight width={24} height={24} fill={ColorValues.gray40} />
      )}
      {rightElement}
    </Flex.Row>
  );
};
